/**
 * Service: SpeechStreamingSession
 * SpecReferenceID: SPEECH-V1
 * Requirements: SPEECH-V1-R07, SPEECH-V1-R08
 *
 * Real-time streaming speech-to-text client. Opens an authenticated WebSocket
 * to the backend relay (WS /api/speech/stream), streams microphone audio as
 * 16 kHz mono PCM16 frames, and surfaces interim (live, display-only) and final
 * transcripts via callbacks.
 *
 * SPEECH-V1-R07: interim hypotheses are surfaced for live display.
 * SPEECH-V1-R08: interim and final transcripts are display-only on the client;
 *                nothing is sent to chat/search here. The consumer decides when
 *                to submit the final transcript to the chat pipeline (on the
 *                user's explicit send) - exactly as typed input.
 */

export interface SpeechStreamingCallbacks {
  /** Connection established and recognizer ready. */
  onReady?: () => void;
  /** Live, non-final hypothesis (display only). */
  onInterim?: (text: string) => void;
  /** Stable final segment transcript. */
  onFinal?: (text: string, confidence: number) => void;
  /** Recognition or transport error. */
  onError?: (message: string) => void;
  /** Session closed (optionally with a reason: inactivity | max_duration). */
  onClosed?: (reason?: string) => void;
}

export interface SpeechStreamingOptions {
  /** Optional BCP-47 language hint (e.g. "en-US"). */
  language?: string;
  /** Auth token; defaults to localStorage "auth-token". */
  token?: string | null;
}

/** Shape of a frame received from the backend relay. */
interface StreamFrame {
  type?: string;
  text?: string;
  confidence?: number;
  reason?: string;
}

/** Target capture format expected by the backend push stream. */
const TARGET_SAMPLE_RATE = 16000;

/**
 * How long to wait after a "stop" frame for the server to drain the final
 * segment and close the socket itself. Only a backstop against a hung or
 * unreachable backend - in the normal case the server closes first and this
 * timer is cleared without firing.
 */
const FINALIZE_TIMEOUT_MS = 5000;

/**
 * Build the WebSocket URL for the streaming relay from the current location.
 * Uses the same "/api" base the REST client uses (no /v1/ segment - Bug
 * #5377), upgraded to ws/wss.
 */
function buildStreamUrl(token: string, language?: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams({ token });
  if (language) {
    params.set('language', language);
  }
  return `${proto}//${window.location.host}/api/speech/stream?${params.toString()}`;
}

/** Convert a Float32 PCM buffer (-1..1) to 16-bit little-endian PCM. */
function floatToPcm16(input: Float32Array): ArrayBuffer {
  const out = new DataView(new ArrayBuffer(input.length * 2));
  for (let i = 0; i < input.length; i += 1) {
    const sample = input[i] ?? 0;
    const s = Math.max(-1, Math.min(1, sample));
    out.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return out.buffer;
}

/**
 * Manages one streaming speech-to-text session (WebSocket + microphone).
 */
export class SpeechStreamingSession {
  private ws: WebSocket | null = null;
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private silentSink: GainNode | null = null;
  private started = false;
  /** Guard timer that force-closes the socket if the server never does. */
  private finalizeTimer: number | null = null;

  /**
   * Open the socket, request the microphone, and begin streaming.
   * Resolves once the socket is open (recognizer readiness arrives via onReady).
   */
  async start(
    callbacks: SpeechStreamingCallbacks,
    options: SpeechStreamingOptions = {}
  ): Promise<void> {
    if (this.started) {
      return;
    }
    this.started = true;

    const token =
      options.token ?? localStorage.getItem('auth-token') ?? '';
    if (!token) {
      callbacks.onError?.('Not authenticated');
      this.started = false;
      return;
    }

    // Request the microphone first so a denial fails before opening the socket.
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
    } catch {
      callbacks.onError?.(
        'Failed to access microphone. Please check permissions.'
      );
      this.started = false;
      return;
    }

    this.ws = new WebSocket(buildStreamUrl(token, options.language));
    this.ws.binaryType = 'arraybuffer';

    this.ws.onopen = () => {
      this.beginCapture();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      let frame: StreamFrame;
      try {
        frame = JSON.parse(event.data as string) as StreamFrame;
      } catch {
        return;
      }
      switch (frame.type) {
        case 'ready':
          callbacks.onReady?.();
          break;
        case 'interim':
          callbacks.onInterim?.(frame.text ?? '');
          break;
        case 'final':
          callbacks.onFinal?.(frame.text ?? '', frame.confidence ?? 1);
          break;
        case 'error':
          callbacks.onError?.(frame.reason ?? 'recognition error');
          break;
        case 'closed':
          callbacks.onClosed?.(frame.reason);
          break;
        case 'ping':
          // keepalive; nothing to do
          break;
        default:
          break;
      }
    };

    this.ws.onerror = () => {
      callbacks.onError?.('Streaming connection error');
    };

    this.ws.onclose = () => {
      if (this.finalizeTimer !== null) {
        window.clearTimeout(this.finalizeTimer);
        this.finalizeTimer = null;
      }
      this.teardownAudio();
      callbacks.onClosed?.();
    };
  }

  /** Wire the microphone graph and start pushing PCM16 frames. */
  private beginCapture(): void {
    if (!this.mediaStream) {
      return;
    }
    // Request a 16 kHz context so frames match the backend push stream.
    const AudioCtx: typeof AudioContext =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    this.audioContext = new AudioCtx({ sampleRate: TARGET_SAMPLE_RATE });

    this.source = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
    // Zero-gain sink keeps the ScriptProcessor firing without echoing audio.
    this.silentSink = this.audioContext.createGain();
    this.silentSink.gain.value = 0;

    this.processor.onaudioprocess = (e: AudioProcessingEvent) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        return;
      }
      const channel = e.inputBuffer.getChannelData(0);
      this.ws.send(floatToPcm16(channel));
    };

    this.source.connect(this.processor);
    this.processor.connect(this.silentSink);
    this.silentSink.connect(this.audioContext.destination);
  }

  /**
   * Stop streaming: ask the server to finalize, and let it close the socket.
   *
   * SPEECH-V1-R07: the microphone is released immediately so the browser's
   * recording indicator clears, but the socket is deliberately left OPEN.
   * The backend responds to a "stop" frame by flushing the buffered audio
   * and draining Azure's last recognition result, then closes the socket
   * itself. Closing here would cut that off, and the final segment - the
   * only output that reaches the input box - would be lost. That is exactly
   * what happened before: interim text appeared while speaking and vanished
   * on stop.
   *
   * A guard timer closes the socket anyway if the server never does, so a
   * hung backend cannot leave the session open indefinitely.
   */
  stop(): void {
    // Release the microphone straight away. No more frames will be sent,
    // which is what tells the backend's receive loop to flush.
    this.teardownAudio();
    this.started = false;

    if (!this.ws) {
      return;
    }

    if (this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify({ type: 'stop' }));
      } catch {
        // Send failed - fall through to the guard close below.
      }

      if (this.finalizeTimer === null) {
        this.finalizeTimer = window.setTimeout(() => {
          this.finalizeTimer = null;
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.close();
          }
        }, FINALIZE_TIMEOUT_MS);
      }
      return;
    }

    // Already closing or closed: nothing left to drain.
    this.ws.close();
  }

  /**
   * Whether this session is still capturing audio.
   *
   * False once stop() has released the microphone, even while the socket is
   * still open draining the final segment - at that point the session is
   * finishing, not recording, and a new one may be started over it.
   */
  isActive(): boolean {
    return this.started;
  }

  /**
   * Abandon this session's callbacks without waiting for it to finish.
   *
   * Used when a new session starts while an old one is still draining: the
   * old session's onClosed would otherwise clear interim text and streaming
   * state belonging to the new one.
   */
  detach(): void {
    if (this.ws) {
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close();
      }
      this.ws = null;
    }
    if (this.finalizeTimer !== null) {
      window.clearTimeout(this.finalizeTimer);
      this.finalizeTimer = null;
    }
    this.teardownAudio();
    this.started = false;
  }

  private teardownAudio(): void {
    try {
      this.processor?.disconnect();
      this.source?.disconnect();
      this.silentSink?.disconnect();
      if (this.audioContext && this.audioContext.state !== 'closed') {
        void this.audioContext.close();
      }
      this.mediaStream?.getTracks().forEach((t) => t.stop());
    } catch {
      // ignore teardown errors
    }
    this.processor = null;
    this.source = null;
    this.silentSink = null;
    this.audioContext = null;
    this.mediaStream = null;
  }
}

export default SpeechStreamingSession;
